import { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { TransactieRij } from './TransactieRij';
import type { ChauffeurKas } from '../../stores/kas-store';

interface ChauffeurKasCardProps {
  chauffeur: ChauffeurKas;
  isUpdating: boolean;
  onMarkeerGecontroleerd: (transactieIds: string[]) => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

export function ChauffeurKasCard({ chauffeur, isUpdating, onMarkeerGecontroleerd }: ChauffeurKasCardProps) {
  const [isOpen, setIsOpen] = useState(!chauffeur.alleGecontroleerd);
  const openTransacties = chauffeur.transacties.filter(t => !t.gecontroleerd);
  const openCount = openTransacties.length;

  const handleMarkeer = () => {
    const ids = openTransacties.map(t => t.id);
    onMarkeerGecontroleerd(ids);
  };

  return (
    <Card variant="bordered" padding="none">
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Chauffeur avatar */}
          <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center">
            <span className="text-slate-600 text-sm font-semibold">
              {chauffeur.naam.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="text-left">
            <p className="font-medium text-main">{chauffeur.naam}</p>
            <p className="text-xs text-muted">
              {chauffeur.aantalTransacties} transactie{chauffeur.aantalTransacties !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Badge variant={chauffeur.alleGecontroleerd ? 'success' : 'warning'} size="sm">
            {chauffeur.alleGecontroleerd
              ? 'Gecontroleerd'
              : `${openCount} open`
            }
          </Badge>
          <span className="text-lg font-semibold text-main">
            {formatCurrency(chauffeur.totaalBedrag)}
          </span>
          {/* Chevron */}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expandable content */}
      {isOpen && (
        <div className="border-t border-gray-100">
          {/* Transaction list */}
          <div className="px-2 py-1">
            {chauffeur.transacties.map(t => (
              <TransactieRij key={t.id} transactie={t} />
            ))}
          </div>

          {/* Action footer — only if open transactions */}
          {openCount > 0 && (
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-muted">
                {formatCurrency(openTransacties.reduce((s, t) => s + t.bedrag, 0))} open
              </p>
              <Button
                size="sm"
                variant="primary"
                isLoading={isUpdating}
                onClick={handleMarkeer}
              >
                Markeer als gecontroleerd
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
